import { DaysOfWeek } from "@/enums/settings";
import type { ScheduleDay, ScheduleHours } from "@/types/profile";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DaysActiveOption = "weekdays" | "weekends" | "allDays" | "custom";
export type TimesActiveOption = "businessHours" | "allHours" | "custom";
export type TFunction = (key: string, opts?: Record<string, string>) => string;

/** Internal slot type with a stable ID for animation keys. */
export type Slot = ScheduleHours & { _id: string };

/** Per-day hours map – stores an array of time slots per day. */
export type DayHoursMap = Record<DaysOfWeek, Slot[]>;

export interface DerivedState {
  daysActive: DaysActiveOption;
  timesActive: TimesActiveOption;
  selectedDays: DaysOfWeek[];
  dayHours: DayHoursMap;
  sameHoursAllWeek: boolean;
  sameHoursAllWeekend: boolean;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const DEFAULT_START = "09:00";
export const DEFAULT_END = "17:00";

export const WEEKDAYS = [
  DaysOfWeek.Monday,
  DaysOfWeek.Tuesday,
  DaysOfWeek.Wednesday,
  DaysOfWeek.Thursday,
  DaysOfWeek.Friday,
];
export const WEEKEND_DAYS = [DaysOfWeek.Saturday, DaysOfWeek.Sunday];
export const ALL_DAYS = [...WEEKDAYS, ...WEEKEND_DAYS];

/** Translation keys for short day labels (Mon, Tue, etc.) */
export const DAY_LABEL_KEYS: Record<DaysOfWeek, string> = {
  [DaysOfWeek.Monday]: "businessHours.days.mon",
  [DaysOfWeek.Tuesday]: "businessHours.days.tue",
  [DaysOfWeek.Wednesday]: "businessHours.days.wed",
  [DaysOfWeek.Thursday]: "businessHours.days.thu",
  [DaysOfWeek.Friday]: "businessHours.days.fri",
  [DaysOfWeek.Saturday]: "businessHours.days.sat",
  [DaysOfWeek.Sunday]: "businessHours.days.sun",
};

/** Translation keys for full day names (Monday, Tuesday, etc.) */
export const DAY_FULL_KEYS: Record<DaysOfWeek, string> = {
  [DaysOfWeek.Monday]: "businessHours.days.mondayFull",
  [DaysOfWeek.Tuesday]: "businessHours.days.tuesdayFull",
  [DaysOfWeek.Wednesday]: "businessHours.days.wednesdayFull",
  [DaysOfWeek.Thursday]: "businessHours.days.thursdayFull",
  [DaysOfWeek.Friday]: "businessHours.days.fridayFull",
  [DaysOfWeek.Saturday]: "businessHours.days.saturdayFull",
  [DaysOfWeek.Sunday]: "businessHours.days.sundayFull",
};

// ---------------------------------------------------------------------------
// Slot creation
// ---------------------------------------------------------------------------

let _nextSlotId = 0;
export function createSlot(start: string, end: string): Slot {
  return { start, end, _id: `s${++_nextSlotId}` };
}

export function createDefaultDayHours(): DayHoursMap {
  const map = {} as DayHoursMap;
  for (const day of ALL_DAYS) {
    map[day] = [createSlot(DEFAULT_START, DEFAULT_END)];
  }
  return map;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatTime(time: string, tf: TFunction): string {
  const [hourStr, minute] = time.split(":");
  const hour = Number.parseInt(hourStr, 10);
  const period = hour < 12 ? tf("businessHours.time.am") : tf("businessHours.time.pm");
  let displayHour = hour;
  if (hour === 0) {
    displayHour = 12;
  } else if (hour > 12) {
    displayHour = hour - 12;
  }
  const minuteDisplay = minute === "00" ? "" : `:${minute}`;
  return `${displayHour}${minuteDisplay}${period}`;
}

export function formatTimeRange(start: string, end: string, tf: TFunction): string {
  return `${formatTime(start, tf)}-${formatTime(end, tf)}`;
}

function getSlotsKey(hours: ScheduleHours[]): string | null {
  const validSlots = hours.filter((slot) => slot.start && slot.end);
  if (validSlots.length === 0) return null;
  return validSlots.map((slot) => `${slot.start}-${slot.end}`).join("|");
}

function formatSlotsKey(slotsKey: string, tf: TFunction): string {
  return slotsKey
    .split("|")
    .map((slotKey) => {
      const [start, end] = slotKey.split("-");
      return formatTimeRange(start, end, tf);
    })
    .join(` ${tf("businessHours.time.and")} `);
}

export function getHoursSummary(schedule: ScheduleDay[], tf: TFunction): string {
  const configuredDays = schedule.filter((s) => s.day && getSlotsKey(s.hours));

  if (configuredDays.length === 0) {
    return tf("businessHours.noHoursConfigured");
  }

  const groups: { days: DaysOfWeek[]; hoursKey: string }[] = [];

  for (const dayEntry of configuredDays) {
    const hoursKey = getSlotsKey(dayEntry.hours);
    if (!hoursKey) continue;

    const existingGroup = groups.find((g) => g.hoursKey === hoursKey);

    if (existingGroup) {
      existingGroup.days.push(dayEntry.day);
    } else {
      groups.push({ days: [dayEntry.day], hoursKey });
    }
  }

  if (groups.length === 0) {
    return tf("businessHours.noHoursConfigured");
  }

  const parts = groups.map((group) => {
    const dayNames = group.days.map((d) => tf(DAY_LABEL_KEYS[d])).join(", ");
    const timeRanges = formatSlotsKey(group.hoursKey, tf);
    return `${dayNames}: ${timeRanges}`;
  });

  return parts.join(` ${tf("businessHours.time.and")} `);
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Convert "HH:MM" to total minutes for comparison. */
export function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

export function validateDaySlots(
  slots: ScheduleHours[],
  dayLabel: string,
  tf: TFunction
): string | null {
  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];

    if (!slot.start || !slot.end) {
      return tf("businessHours.validation.incomplete", { day: dayLabel });
    }

    const startMin = timeToMinutes(slot.start);
    const endMin = timeToMinutes(slot.end);
    if (startMin >= endMin) {
      return tf("businessHours.validation.startAfterEnd", { day: dayLabel });
    }

    if (i > 0) {
      const prevEnd = timeToMinutes(slots[i - 1].end);
      if (startMin < prevEnd) {
        return tf("businessHours.validation.overlap", { day: dayLabel });
      }
    }
  }
  return null;
}

export function validateSchedule(
  selectedDays: DaysOfWeek[],
  dayHours: DayHoursMap,
  tf: TFunction
): string | null {
  if (selectedDays.length === 0) {
    return tf("businessHours.validation.noDays");
  }

  for (const day of selectedDays) {
    const error = validateDaySlots(dayHours[day], tf(DAY_FULL_KEYS[day]), tf);
    if (error) return error;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Comparison
// ---------------------------------------------------------------------------

/** Compare two ScheduleHours arrays for equality. */
export function slotsEqual(a: ScheduleHours[], b: ScheduleHours[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((slot, i) => slot.start === b[i].start && slot.end === b[i].end);
}

// ---------------------------------------------------------------------------
// Derive UI state from an existing ScheduleDay[]
// ---------------------------------------------------------------------------

export function deriveStateFromSchedule(schedule: ScheduleDay[]): DerivedState {
  const dayHours = createDefaultDayHours();

  const configuredDays: DaysOfWeek[] = [];
  for (const entry of schedule) {
    if (entry.hours && entry.hours.length > 0 && entry.hours[0]?.start) {
      dayHours[entry.day] = entry.hours.map((h) => createSlot(h.start, h.end));
      configuredDays.push(entry.day);
    }
  }

  const hasAllWeekdays = WEEKDAYS.every((d) => configuredDays.includes(d));
  const hasAllWeekends = WEEKEND_DAYS.every((d) => configuredDays.includes(d));
  const onlyWeekdays = hasAllWeekdays && !hasAllWeekends && configuredDays.length === 5;
  const onlyWeekends = hasAllWeekends && !hasAllWeekdays && configuredDays.length === 2;

  let daysActive: DaysActiveOption = "custom";
  if (onlyWeekdays) daysActive = "weekdays";
  else if (onlyWeekends) daysActive = "weekends";
  else if (configuredDays.length === 7) daysActive = "allDays";

  const allMatchPreset = (start: string, end: string) =>
    configuredDays.length > 0 &&
    configuredDays.every(
      (d) =>
        dayHours[d].length === 1 && dayHours[d][0].start === start && dayHours[d][0].end === end
    );

  let timesActive: TimesActiveOption = "custom";
  if (allMatchPreset("09:00", "17:00")) timesActive = "businessHours";
  else if (allMatchPreset("00:00", "23:59")) timesActive = "allHours";

  const selectedWeekdays = configuredDays.filter((d) => WEEKDAYS.includes(d));
  const sameHoursAllWeek =
    selectedWeekdays.length <= 1 ||
    selectedWeekdays.every((d) => slotsEqual(dayHours[d], dayHours[selectedWeekdays[0]]));

  const selectedWeekends = configuredDays.filter((d) => WEEKEND_DAYS.includes(d));
  const sameHoursAllWeekend =
    selectedWeekends.length <= 1 ||
    selectedWeekends.every((d) => slotsEqual(dayHours[d], dayHours[selectedWeekends[0]]));

  return {
    daysActive,
    timesActive,
    selectedDays: configuredDays,
    dayHours,
    sameHoursAllWeek,
    sameHoursAllWeekend,
  };
}
